// Resolves a ViewportEngine from the series' SOP Class. The clean extension
// point: radiology SOP classes will route to a cornerstone3D engine here, with
// no change to feature code. Every microscopy image renders via DMV.

import { getString, Tag, type DicomJson } from '../data/dicomTags';
import type { ViewportEngine } from './ViewportEngine';
import { SopClass } from './sopClasses';
import { isSingleFrameImage } from './engines/singleTileWsi';

export class UnsupportedModalityError extends Error {
  constructor(public readonly sopClassUID: string | undefined) {
    super(`No viewer engine for SOP Class ${sopClassUID ?? '(unknown)'}`);
    this.name = 'UnsupportedModalityError';
  }
}

export function sopClassOf(metadata: DicomJson[]): string | undefined {
  for (const ds of metadata) {
    const uid = getString(ds, Tag.SOPClassUID);
    if (uid) return uid;
  }
  return undefined;
}

/**
 * Start fetching the deep-zoom engine's code (DMV and its OpenLayers, some
 * 2.6 MB) without waiting for a slide's metadata to ask for it: a study page
 * calls this as it opens, so a first visit downloads it alongside the study's
 * lists rather than after them. Later calls, and selectEngine's own import,
 * get the same module.
 */
export function preloadEngine(): void {
  void import('./engines/DmvEngine').catch(() => {
    // Nothing to do: selectEngine imports it again and reports the failure.
  });
}

export async function selectEngine(metadata: DicomJson[]): Promise<ViewportEngine> {
  const sopClass = sopClassOf(metadata);

  // Every image goes through the one deep-zoom viewport (DMV, lazy-loaded so the
  // worklist never pulls the large DMV/OpenLayers bundle): tiled pyramidal WSI
  // as-is, and single-frame images (photomicrographs, gross photos, XC, …) as a
  // one-tile pyramid — see engines/singleTileWsi.
  if (sopClass === SopClass.VLWholeSlideMicroscopyImage || isSingleFrameImage(metadata[0])) {
    const { DmvEngine } = await import('./engines/DmvEngine');
    return new DmvEngine();
  }

  throw new UnsupportedModalityError(sopClass);
}
